import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, List, Popconfirm, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { operationRequestsApi } from '../api/endpoints';
import { queryKeys } from '../api/queryKeys';
import type { OperationRequest } from '../api/types';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/PageStates';
import { useStoreContext } from '../context/StoreContext';
import { ALL_KEY, useApprovalActions } from '../hooks/useApprovalActions';
import { groupRequestsByDate } from '../lib/approvalGroups';
import { clientDuplicateGroups, isLookalike } from '../lib/duplicates';
import { formatKopeks } from '../lib/money';

/**
 * «Тасдиклар» — the admin approval queue (SPEC §1). PENDING seller submissions
 * grouped by DATE (same-day entries sit together, oldest day first), the store
 * named on each row. Every date card and the page header carry an «Утвердить
 * все» bulk button (one transactional batch call); each row stays individually
 * approvable/rejectable. Approving materializes the real ledger Operation.
 */
export function ApprovalsPage(): JSX.Element {
  const { t } = useTranslation();
  // Global store context (SPEC §5): a selected store scopes this queue too.
  const { storeId: ctxStoreId, storeIds: ctxStoreIds } = useStoreContext();
  // Approve/reject (incl. the duplicate-confirm dance) is shared with the
  // jadval's pending cells — one implementation, one behaviour.
  const {
    approve,
    approveBatch,
    confirmReject,
    busyId,
    busyGroup,
    approvePending,
    rejectPending,
    batchBusy,
  } = useApprovalActions();

  const scope = { storeId: ctxStoreId, storeIds: ctxStoreIds };
  const query = useQuery({
    queryKey: queryKeys.pendingApprovals(scope),
    queryFn: () => operationRequestsApi.listPending(scope),
  });

  const kindLabel = (req: OperationRequest): string => {
    if (req.type === 'STORE_PAYMENT') {
      return req.paymentMethod ? t(`payMethod.${req.paymentMethod}`) : t('opType.STORE_PAYMENT');
    }
    return req.categoryName ?? t('opType.EXPENSE');
  };

  // Same-day entries together (owner's ask), oldest day on top; the store name
  // moved from the card title down onto each row.
  const groups = groupRequestsByDate(query.data ?? []);
  const allRequests = query.data ?? [];
  const grandTotal = allRequests.reduce((sum, req) => sum + req.amount, 0);
  const clientGroups = clientDuplicateGroups(allRequests);

  /** The «Утвердить все» button + Popconfirm, shared by header and date cards. */
  const approveAllButton = (
    key: string,
    ids: string[],
    sum: number,
    small: boolean,
    duplicates: number,
  ): JSX.Element => (
    <Popconfirm
      title={t('approvals.approveAllTitle')}
      description={
        <div>
          <div>{t('approvals.approveAllConfirm', { n: ids.length, sum: formatKopeks(sum) })}</div>
          {duplicates > 0 ? (
            <div style={{ color: '#d46b08' }}>
              {t('approvals.approveAllHasDuplicates', { n: duplicates })}
            </div>
          ) : null}
        </div>
      }
      okText={t('approvals.approve')}
      cancelText={t('common.cancel')}
      onConfirm={() => approveBatch(key, ids)}
      disabled={batchBusy}
    >
      <Button
        type="primary"
        size={small ? 'small' : 'middle'}
        icon={<CheckOutlined />}
        loading={busyGroup === key && batchBusy}
        disabled={batchBusy && busyGroup !== key}
      >
        {t('approvals.approveAll')} ({ids.length})
      </Button>
    </Popconfirm>
  );

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <Typography.Title level={4} style={{ margin: 0 }}>
          {t('approvals.title')}
        </Typography.Title>
        {allRequests.length > 1
          ? approveAllButton(
              ALL_KEY,
              allRequests.map((req) => req.id),
              grandTotal,
              false,
              groups.reduce((n, g) => n + g.duplicateCount, 0),
            )
          : null}
      </div>

      {query.isLoading ? (
        <LoadingBlock />
      ) : query.isError || !query.data ? (
        <ErrorBlock onRetry={() => void query.refetch()} />
      ) : query.data.length === 0 ? (
        <EmptyBlock description={t('approvals.empty')} />
      ) : (
        groups.map((group) => (
          <Card
            key={group.date}
            size="small"
            title={
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {dayjs(group.date).format('DD.MM.YYYY')}
                <Tag color="gold">{group.requests.length}</Tag>
                <Typography.Text type="secondary" style={{ fontWeight: 'normal' }}>
                  {formatKopeks(group.totalAmount)}
                </Typography.Text>
              </span>
            }
            extra={approveAllButton(
              group.date,
              group.requests.map((req) => req.id),
              group.totalAmount,
              true,
              group.duplicateCount,
            )}
            styles={{ body: { paddingTop: 0, paddingBottom: 0 } }}
          >
            <List
              dataSource={group.requests}
              renderItem={(req) => (
                <List.Item
                  style={{ paddingInline: 4, alignItems: 'flex-start' }}
                  extra={
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Button
                        type="primary"
                        icon={<CheckOutlined />}
                        loading={busyId === req.id && approvePending}
                        disabled={batchBusy}
                        onClick={() => approve(req.id)}
                        aria-label={t('approvals.approve')}
                      />
                      <Button
                        danger
                        icon={<CloseOutlined />}
                        loading={busyId === req.id && rejectPending}
                        disabled={batchBusy}
                        onClick={() => confirmReject(req)}
                        aria-label={t('approvals.reject')}
                      />
                    </div>
                  }
                >
                  <List.Item.Meta
                    title={
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Typography.Text strong>{formatKopeks(req.amount)}</Typography.Text>
                        {req.deductFromStoreBalance ? (
                          <Tag color="volcano">{t('quick.deductFromStore')}</Tag>
                        ) : null}
                        {req.duplicateOperationId ? (
                          <Tag color="red">{t('approvals.duplicateLedgerTag')}</Tag>
                        ) : isLookalike(req, clientGroups) ? (
                          <Tag color="orange">{t('approvals.duplicateTag')}</Tag>
                        ) : null}
                        {req.duplicateConfirmedAt ? (
                          <Tag color="blue">{t('approvals.duplicateSellerConfirmed')}</Tag>
                        ) : null}
                      </span>
                    }
                    description={
                      <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                        {/* The submit time is what tells two identical rows
                            apart at a glance — without it they are literally
                            indistinguishable. */}
                        {req.storeName ?? '—'} · {kindLabel(req)} · {req.createdByName} ·{' '}
                        {dayjs(req.createdAt).format('HH:mm')}
                        {req.comment ? ` · ${req.comment}` : ''}
                      </Typography.Text>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        ))
      )}
    </div>
  );
}
